/**
 * EXC collection tabs, for /collections.
 *
 * The cards render as plain links to each collection page, so the page works with
 * JS off and search engines still crawl through to the real collections. This
 * upgrades them: tapping a card swaps the products underneath instead of loading
 * a new page.
 *
 * Fragments come from templates/collection.inline-grid.liquid, via
 * /collections/<handle>?view=inline-grid, the same trick quick shop already uses.
 */

const VIEW = 'view=inline-grid';

class ExcCollectionTabs extends HTMLElement {
  connectedCallback() {
    this.rail = this.querySelector('.js-exc-tabs-rail');
    this.panel = this.querySelector('.js-exc-tabs-panel');
    if (!this.rail || !this.panel) return;

    this.tabs = Array.from(this.rail.querySelectorAll('[role="tab"]'));
    if (!this.tabs.length) return;

    /** Rendered HTML per handle, so going back to a collection is instant. */
    this.cache = new Map();
    this.controller = null;

    const active = this.dataset.active;
    if (active) this.cache.set(active, this.panel.innerHTML);

    this.prev = this.querySelector('.js-exc-tabs-prev');
    this.next = this.querySelector('.js-exc-tabs-next');

    this.rail.addEventListener('click', this.onRailClick.bind(this));
    this.rail.addEventListener('keydown', this.onRailKeydown.bind(this));
    this.panel.addEventListener('click', this.onPanelClick.bind(this));
    window.addEventListener('popstate', this.onPopState.bind(this));

    this.setupArrows();
    this.setupDrag();

    // A shared or reloaded ?c=<handle> link should open on that collection.
    const wanted = new URLSearchParams(window.location.search).get('c');
    if (wanted && wanted !== active && this.tabFor(wanted)) {
      this.select(wanted, { updateHistory: false });
    }
  }

  /**
   * The rail is one scrolling row at every width. A phone swipes it; a mouse
   * cannot, so desktop gets arrows. They hide themselves when there is nothing
   * left to scroll to, and when the whole row already fits.
   */
  setupArrows() {
    if (!this.prev || !this.next) return;

    this.prev.addEventListener('click', () => this.scrollRail(-1));
    this.next.addEventListener('click', () => this.scrollRail(1));

    // syncArrows reads scrollWidth and clientWidth, which forces layout. Running
    // that raw on every scroll event is enough to make a drag feel sticky, so it
    // is throttled to one read per frame.
    let queued = false;
    const sync = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        this.syncArrows();
      });
    };
    this.rail.addEventListener('scroll', sync, { passive: true });

    if ('ResizeObserver' in window) {
      new ResizeObserver(sync).observe(this.rail);
    } else {
      window.addEventListener('resize', sync);
    }

    this.syncArrows();
  }

  /**
   * Grab and drag the rail with a mouse, the way a thumb drags it on a phone.
   * Mouse only: touch already scrolls natively, and capturing those pointers
   * would take that away.
   */
  setupDrag() {
    let startX = 0;
    let startScroll = 0;
    let pointerId = null;
    let targetScroll = 0;
    let pending = false;

    // Velocity in pixels per millisecond, smoothed so one jittery frame does not
    // decide the whole flick.
    let velocity = 0;
    let lastX = 0;
    let lastTime = 0;

    this.dragged = false;
    this.momentumFrame = null;

    const stopMomentum = () => {
      if (this.momentumFrame === null) return;
      cancelAnimationFrame(this.momentumFrame);
      this.momentumFrame = null;
    };
    this.stopMomentum = stopMomentum;

    // Write scrollLeft once per frame rather than once per pointermove. Mice fire
    // move events faster than the screen redraws, and the extra writes are what
    // makes a drag feel like it is catching.
    const applyScroll = () => {
      pending = false;
      this.rail.scrollLeft = targetScroll;
    };

    this.rail.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      stopMomentum();
      pointerId = event.pointerId;
      startX = event.clientX;
      lastX = event.clientX;
      lastTime = event.timeStamp;
      velocity = 0;
      startScroll = this.rail.scrollLeft;
      targetScroll = startScroll;
      this.dragged = false;
      // is-grabbing goes on at the first real move, not here. It sets
      // pointer-events: none on the cards, so adding it on pointerdown makes the
      // following pointerup and click land on the rail and no card ever selects.
    });

    this.rail.addEventListener('pointermove', (event) => {
      if (pointerId === null || event.pointerId !== pointerId) return;
      const moved = event.clientX - startX;
      // A few pixels of slop, so a slightly shaky click still selects a card.
      if (!this.dragged && Math.abs(moved) < 5) return;

      if (!this.dragged) {
        this.dragged = true;
        this.rail.classList.add('is-grabbing');
        // Only capture once it is a real drag, otherwise the click never lands.
        // Capture throws if the pointer is already gone; the drag still works.
        try {
          this.rail.setPointerCapture(pointerId);
        } catch (error) {
          /* no capture, carry on */
        }
      }

      const elapsed = event.timeStamp - lastTime;
      if (elapsed > 0) {
        const frameVelocity = (lastX - event.clientX) / elapsed;
        velocity = velocity * 0.7 + frameVelocity * 0.3;
        lastX = event.clientX;
        lastTime = event.timeStamp;
      }

      targetScroll = startScroll - moved;
      if (!pending) {
        pending = true;
        requestAnimationFrame(applyScroll);
      }
    });

    const end = () => {
      if (pointerId === null) return;
      try {
        if (this.rail.hasPointerCapture(pointerId)) this.rail.releasePointerCapture(pointerId);
      } catch (error) {
        /* already released */
      }
      pointerId = null;
      this.rail.classList.remove('is-grabbing');

      if (this.dragged) this.glide(velocity);

      // Let the click that follows this pointerup through, then forget the drag.
      window.setTimeout(() => {
        this.dragged = false;
      }, 0);
    };

    this.rail.addEventListener('pointerup', end);
    this.rail.addEventListener('pointercancel', end);

    // A wheel or a fresh grab should kill the glide, not fight it.
    this.rail.addEventListener('wheel', stopMomentum, { passive: true });

    // Stop the browser's own image drag from fighting the rail drag.
    this.rail.addEventListener('dragstart', (event) => event.preventDefault());
  }

  /**
   * Carry a flick after the mouse lets go, decaying like a phone does.
   * Friction is per millisecond, so the feel does not change with refresh rate.
   */
  glide(velocity) {
    if (Math.abs(velocity) < 0.05) return;

    /*
      Tuned by measuring the coast, not by feel alone:
        0.995 / 0.02  throws a long way but creeps the last 39px over 900ms
        0.992 / 0.04  stops clean but only carries 125px, about one card
        0.995 / 0.06  long throw, and it stops while still moving a pixel a frame,
                      which reads as a stop rather than a drift
      Friction is per millisecond, so the feel holds on a 120Hz screen too.
    */
    const friction = 0.995;
    let speed = velocity;
    let last = performance.now();

    const step = (now) => {
      const elapsed = now - last;
      last = now;

      speed *= Math.pow(friction, elapsed);
      if (Math.abs(speed) < 0.06) {
        this.momentumFrame = null;
        return;
      }

      const before = this.rail.scrollLeft;
      this.rail.scrollLeft = before + speed * elapsed;
      // Hit an end: stop rather than spin for another second.
      if (this.rail.scrollLeft === before) {
        this.momentumFrame = null;
        return;
      }

      this.momentumFrame = requestAnimationFrame(step);
    };

    this.momentumFrame = requestAnimationFrame(step);
  }

  syncArrows() {
    if (!this.prev || !this.next) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.rail;
    // Sub-pixel widths mean scrollLeft never quite reaches the end. Allow a pixel.
    const overflows = scrollWidth - clientWidth > 1;

    this.prev.hidden = !overflows || scrollLeft <= 1;
    this.next.hidden = !overflows || scrollLeft >= scrollWidth - clientWidth - 1;
  }

  scrollRail(direction) {
    if (this.stopMomentum) this.stopMomentum();
    const card = this.rail.querySelector('.exc-tabs__card');
    const step = card ? card.getBoundingClientRect().width + 10 : 140;
    // Move most of a screenful, keeping one card of overlap for orientation.
    const jump = Math.max(step, this.rail.clientWidth - step);
    this.rail.scrollBy({ left: jump * direction, behavior: 'smooth' });
  }

  tabFor(handle) {
    return this.tabs.find((tab) => tab.dataset.handle === handle) || null;
  }

  get activeTab() {
    return this.tabFor(this.dataset.active);
  }

  onRailClick(event) {
    const tab = event.target.closest('[role="tab"]');
    if (!tab || !this.rail.contains(tab)) return;

    // Finishing a drag over a card must not open it, or the rail is unusable.
    if (this.dragged) {
      event.preventDefault();
      return;
    }

    // Let modified clicks open the real collection page in a new tab.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    event.preventDefault();
    this.select(tab.dataset.handle);
  }

  onRailKeydown(event) {
    const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
    if (!keys.includes(event.key)) return;

    const current = this.tabs.indexOf(document.activeElement.closest('[role="tab"]'));
    if (current === -1) return;

    event.preventDefault();
    let next = current;
    if (event.key === 'ArrowRight') next = (current + 1) % this.tabs.length;
    if (event.key === 'ArrowLeft') next = (current - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = this.tabs.length - 1;

    this.tabs[next].focus();
    this.select(this.tabs[next].dataset.handle);
  }

  onPanelClick(event) {
    const more = event.target.closest('.js-exc-tabs-more');
    if (!more) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    event.preventDefault();
    this.loadMore(more);
  }

  onPopState() {
    const handle = new URLSearchParams(window.location.search).get('c') || this.tabs[0].dataset.handle;
    if (handle !== this.dataset.active && this.tabFor(handle)) {
      this.select(handle, { updateHistory: false });
    }
  }

  async select(handle, { updateHistory = true } = {}) {
    const tab = this.tabFor(handle);
    if (!tab || handle === this.dataset.active) return;

    this.markActive(tab);
    this.dataset.active = handle;
    if (updateHistory) this.pushHandle(handle);

    if (this.cache.has(handle)) {
      this.panel.innerHTML = this.cache.get(handle);
      this.panel.style.minHeight = '';
      return;
    }

    // Hold the old height while loading so the page does not jump under a thumb.
    this.panel.style.minHeight = `${this.panel.offsetHeight}px`;
    this.panel.setAttribute('aria-busy', 'true');
    this.classList.add('is-loading');

    if (this.controller) this.controller.abort();
    this.controller = new AbortController();

    try {
      const html = await this.fetchGrid(tab.href, this.controller.signal);
      // A faster tap may have landed while this was in flight.
      if (this.dataset.active !== handle) return;
      this.cache.set(handle, html);
      this.panel.innerHTML = html;
    } catch (error) {
      if (error.name === 'AbortError') return;
      // Nothing clever to do here: send them to the real collection page.
      window.location.href = tab.href;
      return;
    } finally {
      if (this.dataset.active === handle) {
        this.panel.style.minHeight = '';
        this.panel.setAttribute('aria-busy', 'false');
        this.classList.remove('is-loading');
      }
    }
  }

  async loadMore(button) {
    if (button.classList.contains('is-loading')) return;
    button.classList.add('is-loading');

    const handle = button.dataset.handle;
    const page = button.dataset.nextPage;
    const url = `${window.Shopify?.routes?.root || '/'}collections/${handle}?${VIEW}&page=${page}`;

    try {
      const html = await this.fetchGrid(url);
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const cards = parsed.querySelector('.js-exc-tabs-cards');
      const nextMore = parsed.querySelector('.js-exc-tabs-more');
      const grid = this.panel.querySelector('.js-exc-tabs-cards');

      if (cards && grid) {
        // Never append a product that is already on screen. If a page param ever
        // goes wrong again, the grid stays correct instead of showing doubles.
        const seen = new Set(
          Array.from(grid.querySelectorAll('[data-product-id]')).map((el) => el.dataset.productId)
        );
        const fresh = Array.from(cards.children).filter((item) => {
          const card = item.querySelector('[data-product-id]');
          return !card || !seen.has(card.dataset.productId);
        });
        grid.append(...fresh);
      }

      if (nextMore) {
        button.dataset.nextPage = nextMore.dataset.nextPage;
        button.href = nextMore.href;
        button.classList.remove('is-loading');
      } else {
        button.closest('.exc-tabs__more-wrap').remove();
      }

      // Keep the cache in step, so leaving and coming back keeps what was loaded.
      this.cache.set(handle, this.panel.innerHTML);
    } catch (error) {
      button.classList.remove('is-loading');
      window.location.href = button.href;
    }
  }

  async fetchGrid(href, signal) {
    const url = new URL(href, window.location.origin);
    url.searchParams.set('view', 'inline-grid');

    const response = await fetch(url.toString(), { signal });
    if (!response.ok) throw new Error(`Collection fetch failed: ${response.status}`);
    return response.text();
  }

  markActive(tab) {
    this.tabs.forEach((item) => {
      const isActive = item === tab;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-selected', String(isActive));
      item.tabIndex = isActive ? 0 : -1;
    });

    // Pull a card that is half off the rail into view.
    if (this.stopMomentum) this.stopMomentum();
    if (typeof tab.scrollIntoView === 'function') {
      tab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
  }

  pushHandle(handle) {
    const url = new URL(window.location.href);
    url.searchParams.set('c', handle);
    window.history.pushState({ handle }, '', url.toString());
  }
}

if (!customElements.get('exc-collection-tabs')) {
  customElements.define('exc-collection-tabs', ExcCollectionTabs);
}
